import { useState, useRef, useEffect } from 'react'
import { GoogleAuthProvider, getAuth, signInWithRedirect, OAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword, linkWithRedirect } from "firebase/auth";
import { validateEmail } from '@/shared/utils';
import { Button, Spacing, Textbox, RadioGroup, RadioOption, Checkbox, HalfSpacing } from '@/form/FormComponents'
import { useLoaderData, useNavigate, useSearchParams } from 'react-router'
import { FormProvider, FormSubmitProvider } from '@/form/FormProvider'
import { useUser, isCompleteUserdata, refreshUserdata, signOut, sendEmailVerification, useWaitForUserToHaveEmailVerified, sendPasswordResetEmail, signInWithEmailCode, verifyEmailCode, signInWithEmailLink, fetchSignInMethodsForEmail, TERMS_VERSION } from '@/systems/Authentication'
import { updateProfile, getMyOrganizationDepartments } from '@/data/ProfileData'
import { Icon } from '@/components/Icon'
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal, useHelpModal, ModalListItem } from '@/components/Modal';
import { useJoinDepartmentModals, useJoinDepartmentModal } from '@/pages/SettingsLayout';
import { publicAction } from '@/data/loaders';

const no_access_message = "Please use your school or work email. You can't sign up with a personal email.";

export function SignedOutLayout() {
  const { user, userdata } = useUser();
  const [hadIncompleteUserdata, setHadIncompleteUserdata] = useState(user && userdata && !isCompleteUserdata(userdata));

  useEffect(() => {
    if (user && userdata && !isCompleteUserdata(userdata)) {
      setHadIncompleteUserdata(true);
    }
    if (!user) {
      setHadIncompleteUserdata(false);
    }
  }, [user, userdata]);

  const [openUnsupportedBrowserModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Please update your browser">
        <ModalContent>
          Your browser is out of date or unsupported, so things won't work right. Please update to the latest version of a modern browser to avoid issues.
          <Spacing/>
          Supported browsers:
          <HalfSpacing/>
          &bull; Google Chrome (version 117 or newer)
          <HalfSpacing/>
          &bull; Microsoft Edge (version 117 or newer)
          <HalfSpacing/>
          &bull; Apple Safari (version 16.0 or newer)
          <HalfSpacing/>
          &bull; Mozilla Firefox (version 71 or newer)
          <Spacing/>
        </ModalContent>
      </Modal>
    )
  })

  const openedBrowserModal = useRef(false);

  useEffect(() => {
    if (!openedBrowserModal.current) {
      openedBrowserModal.current = true;
      if (localStorage.getItem("browser_modal_ignored") != "true") {

        let supported = false;
        try {
          supported = CSS.supports('grid-template-columns', 'subgrid');
        } catch (e) {
          supported = false;
        }

        if (!supported) {
          openUnsupportedBrowserModal();
        }
      }
    }
  }, []);

  if (user && userdata && hadIncompleteUserdata) {
    return <CompleteUserProfile />
  } else {
    return <SignInOrSignUp />
  }
}

function SignInOrSignUp() {
  const initial_last_login_error = useLoaderData().last_login_error;
  const [last_login_error, setLastLoginError] = useState(initial_last_login_error);
  const [initial_email, setInitialEmail] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continue_path = searchParams.get("continue") || "/";

  const [step, setStep] = useState("email"); // "email", "password", "password_register"
  const [register_email, setRegisterEmail] = useState(null);
  const [providerToLink, setProviderToLink] = useState(null);

  const initial_data = {};
  const initial_error = {};

  if (last_login_error?.email) {
    initial_data.email = last_login_error.email;
  }

  if (last_login_error) {
    initial_error.email = last_login_error.message || ((last_login_error.error.code == "auth/internal-error" && last_login_error.error.message && last_login_error.error.message.indexOf("Not eligible domain") > -1) ? no_access_message : last_login_error.error.message);
  }

  if (initial_email) {
    initial_data.email = initial_email;
  }

  const [loading, setLoading] = useState(false);

  const signInWithGoogle = function(email) {
    localStorage.removeItem("providerToLink");
  
    const provider = new GoogleAuthProvider();

    provider.setCustomParameters({
      'login_hint': email
    });

    const auth = getAuth();

    signInWithRedirect(auth, provider);
  }

  const signInWithMicrosoft = function(email) {
    localStorage.removeItem("providerToLink");
          
    const provider = new OAuthProvider('microsoft.com');

    provider.setCustomParameters({
      // prompt: 'consent', // Force re-consent every time
      login_hint: email,
      tenant: "organizations"
    });          
    
    const auth = getAuth();

    signInWithRedirect(auth, provider);
  }

  const handleLoginSubmit = function({ formatted_data, focusError, updateData, updateError }) {
    if (loading) { return; }

    setLastLoginError(null);

    let email = formatted_data.email;
    let password = formatted_data.password;

    if (!validateEmail(email)) {
      focusError({name: "email", error: "Please enter a valid email address"})
      return;
    }

    setLoading(true);

    if (step == "email") {
      publicAction("getLoginProvider", { email }, { unauthenticated: true }).then(function({ data }) {
  
        if (data.provider == "password") {

          fetchSignInMethodsForEmail(email).then(function(methods) {

            if (methods.includes("emailLink") && !methods.includes("password")) {

              signInWithEmailCode(email).then(function({ providerToLink }) {
                setLoading(false);
                setStep("email_link");
                setProviderToLink({ email, provider: providerToLink });
              }).catch(function(error) {
                console.error(error);
                focusError({name: "email", error: "Something went wrong, please try again. If the problem persists, contact support."});
                setLoading(false);
              });

            } else {

              setLoading(false);
              if (data.registered) {
                setStep("password");
                updateError({});
              } else {
                setStep("password_register");
                setRegisterEmail(email);
                updateError({});
              }

            }

          }).catch(function (error) {
            focusError({name: "email", error: `Something went wrong, please try again. If the problem persists, contact support. (Error: ${error.code} ${error.message})`});
            setLoading(false);
            console.error(error);
          });

        } else if (data.provider == "reset-password") {

          sendPasswordResetEmail(email).then(function() {
            setLoading(false);
            setStep("password_reset");
          }).catch(function(error) {
            console.error(error);
            focusError({name: "email", error: "Something went wrong, please try again. If the problem persists, contact support."});
            setLoading(false);
          });

        } else if (data.provider == "email-link") {

          signInWithEmailCode(email).then(function({ providerToLink }) {
            setLoading(false);
            setStep("email_link");
            setProviderToLink({ email, provider: providerToLink });
          }).catch(function(error) {
            console.error(error);
            focusError({name: "email", error: "Something went wrong, please try again. If the problem persists, contact support."});
            setLoading(false);
          });

        } else if (data.provider == "google.com") {

          signInWithGoogle(email);
          
        } else if (data.provider == "microsoft.com") {

          signInWithMicrosoft(email);
  
        } else if (data.provider == "choose-provider") {
          setRegisterEmail(email);
          setStep("choose_provider");
          setLoading(false);
        } else {
          focusError({name: "email", error: "Something went wrong. Please contact support."});
          setLoading(false);
        }
  
      }).catch(function (error) {
        focusError({name: "email", error: (error.status == 404 ? no_access_message : `Something went wrong, please try again. If the problem persists, contact support. (Error code: ${error.message})`)});
        setLoading(false);
        console.error(error);
      });
    } else {
      localStorage.removeItem("providerToLink");
      const auth = getAuth();
      signInWithEmailAndPassword(auth, email, password).then((userCredential) => {
  
        navigate(continue_path, { replace: true });
  
      }).catch((error) => {
  
        setLoading(false);

        updateData((draft) => {
          draft.password = "";
        })
  
        if (error.code == "auth/wrong-password") {
          focusError({name: "password", error: "Incorrect password"});
        } else {
          let errormsg = error.code.replace("auth/invalid-email", "Invalid email").replace("auth/user-not-found", "Account does not exist").replace("auth/user-disabled", "Account suspended. Please contact support.");
          focusError({name: "email", error: errormsg});
          setStep("email");
        }
      });
    }
  }

  const handleCreateSubmit = function({ formatted_data, focusError, updateData }) {
    if (loading) { return; }

    let email = register_email;
    let password = formatted_data.password;
    let password_verify = formatted_data.password_verify;

    if (password !== password_verify) {
      focusError({name: "password_verify", select: true, error: "Passwords don't match"})
      return;
    }

    setLoading(true);

    localStorage.removeItem("providerToLink");
    const auth = getAuth();
    createUserWithEmailAndPassword(auth, email, password).then((userCredential) => {

      navigate(continue_path, { replace: true });
      
    }).catch((error) => {

      setLoading(false);

      if (error.code == "auth/weak-password") {
        focusError({name: "password", error: "Please choose a stronger password"});
        updateData((draft) => {
          draft.password = "";
          draft.password_verify = "";
        })
      } else if (error.code == "auth/email-already-in-use") {
        setLastLoginError({message: "Account already exists. Please sign in again."});
        setStep("email");
      } else if (error.code == "auth/internal-error" && error.message && error.message.indexOf("Not eligible domain") > -1) {
        setLastLoginError({message: no_access_message});
        setStep("email");
      } else {
        focusError({name: "password", error: error.message});
        updateData((draft) => {
          draft.password = "";
          draft.password_verify = "";
        })
      }
    });
  }

  const [openErrorModal] = useErrorModal();

  const [openForgotPasswordModal] = useModal(({ context, closeModal, setLoading }) => {
    const resetPassword = function() {
      setLoading(true);
      sendPasswordResetEmail(context.data.email).then(function() {
        closeModal();
        openForgotPasswordSuccessModal();
      }).catch(function(error) {
        closeModal();
        console.error(error);
        openErrorModal();
      });
    }

    return (
      <Modal title="Forgot your password?">
        <ModalContent>
          We'll email you a link to choose a new password.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Reset password" className="primary" onClick={resetPassword}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openForgotPasswordSuccessModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Check your email">
        <ModalContent>
          We just emailed you a link to choose a new password.
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleEmailCodeSubmit = function({ formatted_data, focusError, updateData }) {
    if (loading) { return; }

    let code = formatted_data.code;

    const emailCodeRegex = /^([A-Za-z0-9]{3}-?[A-Za-z0-9]{3})$/
    if (!emailCodeRegex.test(code)) {
      focusError({name: "code", error: "Invalid sign-in code"});
      return;
    }

    setLoading(true);

    localStorage.removeItem("providerToLink");

    verifyEmailCode(providerToLink.email, code).then(function({ success, oobCode }) {

      if (success) {
      
        signInWithEmailLink(providerToLink.email, oobCode).then(function() {
          
          localStorage.setItem("providerToLink", JSON.stringify(providerToLink));
          navigate(continue_path, { replace: true });

        }).catch(function(error) {
          if (error.code == "auth/invalid-action-code" || error.code == "auth/expired-action-code") {
            focusError({name: "code", error: 'Code has expired. Please click "Back to sign in" and try again.'});
          } else {
            console.error(error);
            focusError({name: "code", error: "Something went wrong, please try again. If the problem persists, contact support."});
          }
          setLoading(false);
        });

      } else {
        focusError({name: "code", error: "Invalid sign-in code"});
        setLoading(false);
      }

    }).catch(function(error) {
      console.error(error);
      focusError({name: "code", error: "Something went wrong, please try again. If the problem persists, contact support."});
      setLoading(false);
    });
  }

  if (step == "password_reset") {
    return (
      <SignInContainer>
        <div className="setup_title">Check your email</div>
        <div className="setup_description">From now on, you'll sign in to your account with a new password created specifically for Sapient Clinician, instead of using your work/school password. Please check your email for a link to choose your new password.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Back to sign in" className="hollow" onClick={() => setStep("email")}/>
        </div>
      </SignInContainer>
    )
  }

  if (step == "email_link") {
    return (
      <SignInContainer loading={loading}>
        <div className="setup_title">Check your email</div>
        <div className="setup_description">We just emailed you a sign-in code. Please enter it below to continue.</div>
        <FormProvider key="email_link" onSubmit={handleEmailCodeSubmit} obviated={loading}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <Spacing/>
                <Textbox name="code" required="Please enter the sign-in code we emailed you" title="Sign-in code" placeholder="XXX-XXX" onEnter={handleSubmit} autofocus={true}/>
                <Spacing/>
                <Spacing/>
                <div className="buttons right">
                  <Button text="Back to sign in" className="hollow" onClick={() => setStep("email")}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </div>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </SignInContainer>
    )
  }

  if (step == "choose_provider") {
    return (
      <SignInContainer>
        <div className="setup_title">How does your organization sign in?</div>
        <HalfSpacing/>
        <div className="buttons social_options">
          <Button text="Sign in with Google" icon="google" className="hollow" onClick={() => signInWithGoogle(register_email)}/>
          <Button text="Sign in with Microsoft" icon="microsoft" className="hollow" onClick={() => signInWithMicrosoft(register_email)}/>
          <Button text="Skip / I'm not sure" className="hollow" onClick={() => setStep("password_register")}/>
        </div>
        <Spacing/>
      </SignInContainer>
    )
  }

  return (
    <SignInContainer loading={loading}>
      {
        (step == "email" || step == "password") ? (
          <FormProvider key="login" onSubmit={handleLoginSubmit} initial_data={initial_data} initial_error={initial_error} obviated={loading}>
            <FormSubmitProvider>
              {({handleSubmit}) => (
                <form onSubmit={(event) => event.preventDefault()} className="pmf-login">
                  {step == "email" ? <div className="setup_title">Sign in or create account</div> : null}
                  <Textbox name="email" required="Please enter your email" title="Email" placeholder="Email" hint={step == "email" ? "Use your work or school email" : null}
                    onEnter={() => {
                      if (step == "email") {
                        handleSubmit({})
                      }
                    }} 
                    onChange={() => {
                      if (step == "password") {
                        setStep("email");
                      }
                    }}
                    autofocus={step == "email" ? (initial_email ? "always" : true) : false} autoComplete="email" />
                  <div style={{display: (step == "password" ? "contents" : "none")}}>
                    <Spacing/>
                    <Textbox name="password" required={step == "password" ? "Please enter your password" : false} title="Password" placeholder="Password" 
                      onEnter={() => {
                        if (step == "email") {
                          handleSubmit({})
                        }
                      }} 
                      autofocus={step == "password"} type="password" autoComplete="password"/>
                  </div>
                  <Spacing/>
                  <Spacing/>
                  <div className="buttons right">
                    {step == "password" ? <Button text="Forgot password" className="hollow" onClick={() => {handleSubmit({ validate: false, callback: openForgotPasswordModal })}}/> : null}
                    <Button text={step == "email" ? "Next" : "Sign in"} key="next" className="primary" submit onClick={handleSubmit}/>
                  </div>
                </form>
              )}
            </FormSubmitProvider>
          </FormProvider>
        ) : (
          <FormProvider key="register" onSubmit={handleCreateSubmit} obviated={loading}>
            <FormSubmitProvider>
              {({handleSubmit}) => (
                <form onSubmit={(event) => event.preventDefault()} className="pmf-registration">
                  <div className="setup_title">Create your account</div>
                  <Textbox name="email" default_value={register_email} required={true} title="Email" placeholder="Email" autoComplete="email" onChange={(value) => {
                    setStep("email");
                    setInitialEmail(value);
                  }} />
                  <Spacing/>
                  <Textbox name="password" title="Choose password" required="Please choose a new password" placeholder="Password" onEnter={() => {}} autofocus={true} type="password"/>
                  <Spacing/>
                  <Textbox name="password_verify" title="Re-type password" required="Please re-type your new password" placeholder="Password" onEnter={handleSubmit} type="password"/>
                  <Spacing/>
                  <Spacing/>
                  <div className="buttons right">
                    <Button text="Create account" className="primary" submit onClick={handleSubmit}/>
                  </div>
                </form>
              )}
            </FormSubmitProvider>
          </FormProvider>
        )
      }
    </SignInContainer>
  )
}

function getMissingPart(user, userdata) {
  const providerToLink = JSON.parse(localStorage.getItem("providerToLink") || "null");
  if (providerToLink && providerToLink.email == user.email && !user.providerData.find(a => a.providerId == providerToLink.provider)) {
    return "email_link";
  } else if (!user.emailVerified && user.email) {
    return "email_verify";
  } else if (!userdata.terms_accepted || !userdata.name || !userdata.role) {
    return "profile";
  } else if (userdata.terms_accepted !== TERMS_VERSION) {
    return "terms_updated";
  } else if (!(userdata.organization_id || userdata.approved)) {
    return "organization_verification";
  } else {
    return null;
  }
}

function CompleteUserProfile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continue_path = searchParams.get("continue") || "/";
  const providerToLink = JSON.parse(localStorage.getItem("providerToLink") || "null");

  const [loading, setLoading] = useState(false);
  const { user, userdata } = useUser();
  const [step, setStep] = useState(getMissingPart(user, userdata));
  const [departments_data, setDepartmentsData] = useState();

  useEffect(() => {
    let newstep = getMissingPart(user, userdata);
    if (newstep) {
      setStep(newstep);
      setLoading(false);
    }
  }, [user, userdata]);

  const handleSubmitTerms = function({ formatted_data, focusError }) {
    if (loading) { return; }

    if (!formatted_data.terms) {
      focusError({name: "terms", error: "Please accept our Privacy Policy and Terms of Use"});
      return;
    }

    setLoading(true);

    updateProfile({ terms_accepted: TERMS_VERSION }).then(function(data) {
      refreshUserdata();
      navigate(continue_path, { replace: true });
    }).catch(function(error) {
      focusError({name: "terms", error: "Something went wrong, please try again."});
      setLoading(false);
      console.error(error);
    });

  }

  const handleSubmitProfile = function({ formatted_data, focusError }) {
    if (loading) { return; }

    if (!formatted_data.terms) {
      focusError({name: "terms", error: "Please accept our Privacy Policy and Terms of Use"});
      return;
    }

    setLoading(true);

    let name = formatted_data.name;
    let role = formatted_data.role;

    if (role == "educator" && userdata.organization_id && false) { // Skip department step for now

      updateProfile({ name, terms_accepted: TERMS_VERSION }).then(function(data) {

        getMyOrganizationDepartments().then(function(data) {

          setLoading(false);
          setDepartmentsData(data.data);
          setStep("department");
  
        }).catch(function(error) {
          focusError({name: "terms", error: "Something went wrong, please try again."});
          setLoading(false);
          console.error(error);
        })

      }).catch(function(error) {
        focusError({name: "terms", error: "Something went wrong, please try again."});
        setLoading(false);
        console.error(error);
      });

    } else {

      updateProfile({ name, role, terms_accepted: TERMS_VERSION }).then(function(data) {
        refreshUserdata();
        navigate(continue_path, { replace: true });
      }).catch(function(error) {
        focusError({name: "terms", error: "Something went wrong, please try again."});
        setLoading(false);
        console.error(error);
      });

    }

  }

  const [openErrorModal] = useErrorModal();

  const [openEmailErrorModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Can't resend verification email right now">
        <ModalContent>
          Please wait a few minutes before requesting a new verification email. It might take a few minutes for the email to arrive. Make sure to check your spam folder.
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });

  const [openEmailHelpModal] = useModal(({ closeModal, setLoading }) => {
    function resendEmail() {
      setLoading(true);
      sendEmailVerification().then(function() {
        closeModal();
      }).catch(function(error) {
        closeModal();
        if (error.message == "Too many attempts") {
          openEmailErrorModal();
        } else {
          openErrorModal();
        }
      });
    }

    return (
      <Modal title="Having trouble?">
        <ModalContent>
          It might take a few minutes for the verification email to arrive. Make sure to check your spam folder.
          <Spacing/>
          <Button text="Resend verification email" onClick={resendEmail}/>
          <Spacing/>
          Still having trouble? <a href="mailto:support@sapientclinician.com" className="links">Contact support</a>.
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });

  const hasSentEmail = useRef(false);

  useEffect(() => {
    if (step == "email_verify" && !hasSentEmail.current) {
      hasSentEmail.current = true;
      sendEmailVerification().then(function() {
        
      }).catch(function(error) {
        console.error(error);
      });
    }
  }, [step]);

  useWaitForUserToHaveEmailVerified({ enabled: step == "email_verify", callback: function() {
    setLoading(true);
    refreshUserdata();
    navigate(continue_path, { replace: true });
  } });

  const linkProviderContinue = function() {
    setLoading(true);

    if (providerToLink.provider == "microsoft.com") {
      const provider = new OAuthProvider("microsoft.com");

      provider.setCustomParameters({
        login_hint: providerToLink.email,
        tenant: "organizations"
      });

      const auth = getAuth();

      linkWithRedirect(auth.currentUser, provider).then(function() {

      }).catch(function(error) {
        if (error.code == "auth/provider-already-linked") {
          window.location.reload();
        } else {
          console.error(error);
          openErrorModal();
          setLoading(false);
        }
      });
    } else {
      openErrorModal();
      setLoading(false);
    }
  }

  const { openRequestToJoinDepartmentModal } = useJoinDepartmentModals({
    onSuccess: function() {
      setStep("department_success");
    },
    onSkip: function() {
      setRoleToEducator();
    }
  });

  const [openJoinDepartmentModal] = useJoinDepartmentModal({ callback: openRequestToJoinDepartmentModal });

  const handleJoinDepartmentClick = function({ department }) {
    if (department.children_type == "organizations") {
      openJoinDepartmentModal({ ...departments_data, initial_parent: department._id });
    } else {
      openRequestToJoinDepartmentModal({ department });
    }
  }

  const setRoleToEducator = function() {
    setLoading(true);

    updateProfile({ role: "educator" }).then(function(data) {
      refreshUserdata();
      navigate(continue_path, { replace: true });
    }).catch(function(error) {
      setLoading(false);
      openErrorModal();
      console.error(error);
    });
  }

  const [openSkipDepartmentModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Skip joining a department?">
      <ModalContent>
        {"Your access is limited until you join a department."}
        <Spacing/>
        {"If your department isn't listed, contact the Sapient Clinician team to sign up."}
      </ModalContent>
      <ModalButtons>
        <Button text="Cancel" className="hollow" onClick={closeModal}/>
        <Button text="Skip for now" onClick={() => {setRoleToEducator(); closeModal();}}/>
      </ModalButtons>
    </Modal>
    )
  })

  if (step == "email_link") {
    return (
      <SignInContainer loading={loading}>
        <div className="setup_title">Sign in with {{"microsoft.com": "Microsoft", "google.com": "Google"}[providerToLink.provider]}</div>
        <div className="setup_description">From now on, you'll use your {{"microsoft.com": "Microsoft", "google.com": "Google"}[providerToLink.provider]} account to sign in.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons right">
          <Button text="Sign out" className="hollow" onClick={signOut}/>
          <Button text="Continue" className="primary" submit onClick={linkProviderContinue}/>
        </div>
      </SignInContainer>
    )
  } else if (step == "email_verify") {
    return (
      <SignInContainer loading={loading}>
        <div className="setup_title">Verify your email</div>
        <div className="setup_description">Please click the verification link in the email we sent to <span style={{fontWeight: "500"}}>{user.email}</span>.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Sign out" className="hollow" onClick={signOut}/>
          <Button text="Having trouble?" className="hollow" onClick={openEmailHelpModal}/>
        </div>
      </SignInContainer>
    )
  } else if (step == "terms_updated") {
    return (
      <SignInContainer loading={loading}>
        <FormProvider key="terms" onSubmit={handleSubmitTerms} obviated={loading}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <div className="setup_title">Privacy and Terms</div>
                <div className="setup_description">Our Privacy Policy and Terms of Use have been updated. To continue, please read and agree to the new version.</div>
                <Spacing/>
                <Checkbox name="terms" text={<>I have read and agree to the <nobr><a href="https://sapientclinician.com/privacy" target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="links">Privacy Policy</a></nobr> and <nobr><a href="https://sapientclinician.com/terms" target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="links">Terms of Use</a></nobr>.</>} autofocus={true}/>
                <Spacing/>
                <Spacing/>
                <div className="buttons right">
                  <Button text="Sign out" className="hollow" onClick={signOut}/>
                  <Button text="Continue" className="primary" submit onClick={handleSubmit}/>
                </div>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </SignInContainer>
    )
  } else if (step == "profile") {
    return (
      <SignInContainer loading={loading}>
        <FormProvider key="name" onSubmit={handleSubmitProfile} obviated={loading} initial_data={{name: userdata.name, role: userdata.role}}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <div className="setup_title">Complete your profile</div>
                <Textbox name="name" title="Your name" placeholder="First Last" autofocus={true} onEnter={handleSubmit} required="Please enter your name"/>
                <Spacing/>
                <RadioGroup title="I am a..." name="role" required="Please select your role">
                  <RadioOption name="student" text="Student"/>
                  <RadioOption name="educator" text="Educator or staff"/>
                </RadioGroup>
                <Spacing/>
                <Checkbox name="terms" text={<>I have read and agree to the <nobr><a href="https://sapientclinician.com/privacy" target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="links">Privacy Policy</a></nobr> and <nobr><a href="https://sapientclinician.com/terms" target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="links">Terms of Use</a></nobr>.</>} autofocus={true}/>
                <Spacing/>
                <Spacing/>
                <div className="buttons right">
                  <Button text="Sign out" className="hollow" onClick={signOut}/>
                  <Button text="Continue" className="primary" submit onClick={handleSubmit}/>
                </div>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </SignInContainer>
    )
  } else if (step == "department") {
    return (
      <SignInContainer loading={loading}>
        <div className="setup_title">{"Join a department for educator access"}</div>
        <div className="setup_description">You need educator access to create scenarios and courses. Join or sign up your department to gain educator access.</div>
        <Spacing/>
        <label style={{borderBottom: "1px solid var(--line)", paddingBottom: "12px"}}>{`Departments at ${userdata.organization_data.shortname}`}</label>
        {[...departments_data.departments].filter(d => (departments_data.departments.length > 1 ? (d.parent == userdata.organization_id) : true)).sort((a, b) => a.name.localeCompare(b.name)).map((department) => <ModalListItem key={department._id} icon="domain" text={department.name} onClick={() => {handleJoinDepartmentClick({department});}}/>)}
        <Spacing/>
        <div style={{fontSize: "18px", color: "var(--btext)"}}>{"Don’t see your department? Contact the Sapient Clinician team to sign up."}</div>
        <Spacing/>
        <Spacing/>
        <div className="buttons right">
          <Button text="Sign out" className="hollow" onClick={signOut}/>
          <Button text="Skip for now" onClick={openSkipDepartmentModal}/>
        </div>
      </SignInContainer>
    )
  } else if (step == "department_success") {
    return (
      <SignInContainer loading={loading}>
        <div className="setup_title">{"Request sent!"}</div>
        <div className="setup_description">{"We'll email you once your request is approved or denied. In the meantime, you can continue with limited access."}</div>
        <Spacing/>
        <Spacing/>
        <div className="buttons right">
          <Button text="Continue" className="primary" onClick={setRoleToEducator}/>
        </div>
      </SignInContainer>
    )
  } else if (step == "organization_verification") {
    return (
      <SignInContainer loading={loading}>
        <div className="setup_title">{"Hang tight!"}</div>
        <div className="setup_description">{"We need to verify your organization before you can continue. We’ll email you as soon as access is approved, usually within 24 hours."}</div>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Sign out" className="hollow" onClick={signOut}/>
        </div>
      </SignInContainer>
    )
  } else {
    return null;
  }
}

// Used by WaitlistLayout
export function SignInContainer({ loading, children, hide_logo = false }) {
  const [openHelpModal] = useHelpModal();

  return (
    <div className="sign_in_container">
      <div className="sign_in_outer_outer">
        <div className={`sign_in_outer ${loading ? "loading" : ""}`}>
          <div id="login_loader_inner" className="indeterminate">
            <div />
          </div>
          <div className="sign_in_inner">
            {!hide_logo && (
              <div className="sign_in_logo">
                <Icon name="Lockup"/>
              </div>
            )}
            <div className="sign_in_form">
              {children}
            </div>
          </div>
        </div>
        <div className="sign_in_footer"><span>© 2025 Sapient Clinician LLC</span><a onClick={openHelpModal} tabIndex={0}>Help</a><a href="https://sapientclinician.com/privacy" target="_blank">Privacy</a><a href="https://sapientclinician.com/terms" target="_blank">Terms</a></div>
      </div>
    </div>
  )
}